// Detect a client's quoted reply history so the UI can split it off.

export interface QuoteStrategy {
  /** Contiguous quote nodes, or [] if none. */
  findQuoteNodes: (root: DocumentFragment) => ChildNode[];
  name: string;
}

const withFollowingSiblings = (start: ChildNode): ChildNode[] => {
  const nodes: ChildNode[] = [start];
  for (let n = start.nextSibling; n; n = n.nextSibling) nodes.push(n);
  return nodes;
};

// Gmail: a self-contained `gmail_quote` element. `*=` tolerates Outlook's
// `x_` round-trip prefix (x_gmail_quote).
export const gmailStrategy: QuoteStrategy = {
  name: 'gmail',
  findQuoteNodes(root) {
    const el = root.querySelector('[class*="gmail_quote"]');
    return el ? [el] : [];
  },
};

// Outlook: `divRplyFwdMsg` header; quote body follows as siblings. `$=` tolerates
// Outlook's `x_` round-trip prefix (x_divRplyFwdMsg, x_x_divRplyFwdMsg).
export const outlookStrategy: QuoteStrategy = {
  name: 'outlook',
  findQuoteNodes(root) {
    const header = root.querySelector('[id$="divRplyFwdMsg"]');
    return header ? withFollowingSiblings(header) : [];
  },
};

export const QUOTE_STRATEGIES: QuoteStrategy[] = [gmailStrategy, outlookStrategy];

export interface SplitBody {
  hasQuote: boolean;
  // Visible message + the quote re-wrapped as a Gmail-style blockquote.
  styledFullHtml: string;
  // Visible message only (quote removed).
  visibleHtml: string;
}

const GMAIL_QUOTE_STYLE = 'margin:0 0 0 .8ex;border-left:1px #ccc solid;padding-left:1ex';

/** Split a body into visible content vs. its quoted history, re-styling the
 *  quote as a Gmail blockquote so any client reads clean. <template> keeps
 *  inline <style> intact. */
export const splitQuotedHistory = (html: string | null | undefined): SplitBody => {
  if (!html) return { hasQuote: false, styledFullHtml: '', visibleHtml: '' };

  const template = document.createElement('template');
  template.innerHTML = html;

  // Cut at the quote that starts EARLIEST in the document, not the first strategy
  // in the list. A mixed Gmail+Outlook thread has both markers; cutting at a
  // deeper one would leave the outer quote visible.
  let match: { name: string; nodes: ChildNode[] } | null = null;
  for (const strategy of QUOTE_STRATEGIES) {
    const nodes = strategy.findQuoteNodes(template.content);
    if (!nodes.length) continue;
    const isEarlier =
      !match ||
      (match.nodes[0].compareDocumentPosition(nodes[0]) & Node.DOCUMENT_POSITION_PRECEDING) !== 0;
    if (isEarlier) match = { name: strategy.name, nodes };
  }

  if (!match) return { hasQuote: false, styledFullHtml: html, visibleHtml: html };

  // Moving the nodes into a holder removes them from the fragment and serializes
  // them in one pass.
  const holder = document.createElement('div');
  match.nodes.forEach((node) => holder.appendChild(node));
  const visibleHtml = template.innerHTML;
  // Gmail quotes already carry the bordered styling; only wrap the rest.
  const quote =
    match.name === 'gmail'
      ? holder.innerHTML
      : `<blockquote class="gmail_quote" style="${GMAIL_QUOTE_STYLE}">${holder.innerHTML}</blockquote>`;
  return { hasQuote: true, styledFullHtml: visibleHtml + quote, visibleHtml };
};
